import type { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { useCallback, useEffect } from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

export const useZoomControls = (
  boxes: Box[],
  imgNatural: { w: number; h: number } | null,
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>,
  resetToFit?: () => void,
  onResetCallback?: () => void,
  onZoomIn?: () => void,
  onZoomOut?: () => void,
  minScale?: number
) => {
  const panToCenter = useCallback(
    (imgCenterX: number, imgCenterY: number, scale: number) => {
      if (!transformRef.current || !imgNatural) return

      const wrapper = transformRef.current.instance.wrapperComponent
      const contentComponent = transformRef.current.instance.contentComponent
      if (!wrapper || !contentComponent) return

      const wrapRect = wrapper.getBoundingClientRect()
      const contentRect = contentComponent.getBoundingClientRect()
      const currentScale = transformRef.current.instance.transformState.scale

      const scaleX = contentRect.width / currentScale / imgNatural.w
      const scaleY = contentRect.height / currentScale / imgNatural.h

      const posX = wrapRect.width / 2 - imgCenterX * scaleX * scale
      const posY = wrapRect.height / 2 - imgCenterY * scaleY * scale

      transformRef.current.setTransform(posX, posY, scale, 200)
    },
    [imgNatural, transformRef]
  )

  const zoomToBox = useCallback(
    (id: string, zoomLevel = 1.5) => {
      const box = boxes.find((b) => b.id === id)
      if (!box) return
      panToCenter(box.x + box.w / 2, box.y + box.h / 2, zoomLevel)
    },
    [boxes, panToCenter]
  )

  const panToRegion = useCallback(
    (bbox: NormalizedBBox) => {
      if (!imgNatural || !transformRef.current) return
      const scale = transformRef.current.instance.transformState.scale
      panToCenter(
        (bbox.left + bbox.width / 2) * imgNatural.w,
        (bbox.top + bbox.height / 2) * imgNatural.h,
        scale
      )
    },
    [imgNatural, transformRef, panToCenter]
  )

  /**
   * Center AND zoom a cited region into view: the region's larger dimension
   * fills ~30% of the viewport, clamped between the fit-to-viewport scale
   * (so large regions land fully visible, never cropped) and 6. Returns
   * false while the viewer isn't ready (image not measured yet).
   */
  const frameRegion = useCallback(
    (bbox: NormalizedBBox): boolean => {
      if (!imgNatural || !transformRef.current) return false
      const wrapper = transformRef.current.instance.wrapperComponent
      const contentComponent = transformRef.current.instance.contentComponent
      if (!wrapper || !contentComponent) return false

      const wrapRect = wrapper.getBoundingClientRect()
      const contentRect = contentComponent.getBoundingClientRect()
      if (wrapRect.width === 0 || contentRect.width === 0) return false
      const currentScale = transformRef.current.instance.transformState.scale

      // Rendered page size at scale 1, then the region's on-screen size.
      const regionW = Math.max(
        bbox.width * (contentRect.width / currentScale),
        1
      )
      const regionH = Math.max(
        bbox.height * (contentRect.height / currentScale),
        1
      )
      const target = Math.min(
        6,
        Math.max(
          minScale ?? 0.05,
          Math.min(
            (0.3 * wrapRect.width) / regionW,
            (0.3 * wrapRect.height) / regionH
          )
        )
      )
      panToCenter(
        (bbox.left + bbox.width / 2) * imgNatural.w,
        (bbox.top + bbox.height / 2) * imgNatural.h,
        target
      )
      return true
    },
    [imgNatural, transformRef, panToCenter, minScale]
  )

  const resetZoom = useCallback(() => {
    if (resetToFit) {
      resetToFit()
    } else if (transformRef.current) {
      transformRef.current.resetTransform(200)
    }
    onResetCallback?.()
  }, [resetToFit, transformRef, onResetCallback])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLElement &&
        ['INPUT', 'TEXTAREA'].includes(event.target.tagName)
      ) {
        return
      }

      if (event.code === 'Space') {
        event.preventDefault()
        resetZoom()
      } else if (event.key === '=' || event.key === '+') {
        event.preventDefault()
        onZoomIn?.()
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault()
        onZoomOut?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [resetZoom, onZoomIn, onZoomOut])

  return { zoomToBox, panToRegion, frameRegion, resetZoom }
}
